import { normalizeProvinciaId, normalizePoblacionId } from "./normalizeLocalizacionIds";

/** Comparación insensible a acentos y mayúsculas. */
export function normLocalidadNombre(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/\s+/g, " ");
}

/**
 * @param {string} nombre — provincia devuelta por Google Places
 * @param {{ id: string, nombre: string }[]} provincias
 */
export function resolverProvinciaId(nombre, provincias) {
  let n = normLocalidadNombre(nombre);
  if (!n) return "";
  n = n.replace(/^provincia de\s+/, "").replace(/^provincia\s+/, "").trim();
  if (!n) return "";

  for (const p of provincias) {
    const pn = normLocalidadNombre(p.nombre);
    if (pn === n) return normalizeProvinciaId(p.id);
  }
  for (const p of provincias) {
    const pn = normLocalidadNombre(p.nombre);
    if (pn.length >= 3 && (n.includes(pn) || pn.includes(n))) {
      return normalizeProvinciaId(p.id);
    }
  }
  return "";
}

/** Quita artículo inicial (La Llosa… ↔ Llosa…). */
export function sinArticuloLocalidad(s) {
  return normLocalidadNombre(s).replace(/^(la|el|les|els|l['’])\s+/, "").trim();
}

/**
 * @param {string} nombre — municipio de Google Places
 * @param {{ id: string, nombre: string }[]} poblaciones
 */
export function resolverPoblacionIdEnLista(nombre, poblaciones) {
  const n = normLocalidadNombre(nombre);
  if (!n || !poblaciones?.length) return "";
  const nSin = sinArticuloLocalidad(n);

  const etiquetas = (label) => {
    const base = String(label || "");
    const parts = [base];
    if (base.includes("/")) {
      base.split("/").forEach((p) => {
        const t = p.trim();
        if (t) parts.push(t);
      });
    }
    return parts;
  };

  for (const p of poblaciones) {
    for (const et of etiquetas(p.nombre)) {
      const pn = normLocalidadNombre(et);
      if (pn === n || sinArticuloLocalidad(pn) === nSin) return normalizePoblacionId(p.id);
    }
  }
  for (const p of poblaciones) {
    for (const et of etiquetas(p.nombre)) {
      const pn = normLocalidadNombre(et);
      const pnSin = sinArticuloLocalidad(pn);
      if (pn.length >= 3 && (n.includes(pn) || pn.includes(n) || nSin.includes(pnSin) || pnSin.includes(nSin))) {
        return normalizePoblacionId(p.id);
      }
    }
  }
  return "";
}

/** Provincia/país / comarca — tramos de dirección, no municipio del pin. */
const PROVINCIA_O_PAIS_RE =
  /^(alicante|alacant|valencia|valència|castell[oó]n|castello de la plana|españa|spain|es|comunitat valenciana|comunidad valenciana|marina alta|la marina alta)$/i;

export function esNombreProvinciaOPais(s) {
  const n = normLocalidadNombre(s);
  if (!n) return false;
  return PROVINCIA_O_PAIS_RE.test(n);
}

const VIA_PREFIX_RE =
  /^(avinguda|avenida|carrer|calle|c\/|passeig|paseo|pla[cç]a|plaza|cam[ií]|camino|passatge|carretera|urb\.?|urbanizaci[oó]n)\b/i;

/**
 * Segmentos de una dirección formateada sin cola provincia/país.
 * Así «…, Jávea, Alicante, España» aporta Jávea y no dispara Alacant/Alicante.
 * En capital: «…, Alicante, Alicante, España» deja un Alicante (municipio).
 */
export function segmentosLocalidadDesdeTexto(texto, provinciaNombre = "") {
  const parts = String(texto || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  while (parts.length && /^(españa|spain|es|comunitat valenciana|comunidad valenciana)$/i.test(parts[parts.length - 1])) {
    parts.pop();
  }
  const prov = normLocalidadNombre(provinciaNombre);
  if (parts.length && prov && normLocalidadNombre(parts[parts.length - 1]) === prov) {
    parts.pop();
  }
  if (parts.length >= 2 && esNombreProvinciaOPais(parts[parts.length - 1])) {
    parts.pop();
  }
  if (parts.length >= 2 && /marina alta/i.test(parts[parts.length - 1])) {
    parts.pop();
  }
  return parts.map((s) => s.replace(/^\d{5}\s+/, "").trim()).filter(Boolean);
}

/** Extrae población del texto de sugerencia Places ("Calle X, Adsubia"). */
export function extraerPoblacionDesdeSugerencia(suggestionText) {
  const parts = String(suggestionText || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 2) return "";
  for (let i = parts.length - 1; i >= 1; i -= 1) {
    let last = parts[i];
    if (!last || last.length < 3 || /^\d+$/.test(last)) continue;
    if (esNombreProvinciaOPais(last)) continue;
    if (VIA_PREFIX_RE.test(last)) continue;
    last = last.replace(/^\d{5}\s+/, "").trim();
    if (!last || last.length < 3 || /^\d+$/.test(last)) continue;
    if (esNombreProvinciaOPais(last)) continue;
    return last;
  }
  return "";
}

/** Prefiere pedanía de la sugerencia cuando Google devuelve el municipio administrativo. */
export function resolverPoblacionPlaces(parts) {
  const apiPob = String(parts?.poblacion || "").trim();
  const fromSuggest = extraerPoblacionDesdeSugerencia(parts?.suggestion_text);
  if (!fromSuggest) return apiPob;
  if (!apiPob) return fromSuggest;
  const ns = normLocalidadNombre(fromSuggest);
  const na = normLocalidadNombre(apiPob);
  if (ns === na) return apiPob;
  if (na.includes(ns) || ns.includes(na)) {
    return apiPob.length >= fromSuggest.length ? apiPob : fromSuggest;
  }
  return fromSuggest;
}

/**
 * Candidatos de población desde Places.
 * - Candidatos explícitos del API / Nominatim: se aceptan tal cual (también Alicante ciudad).
 * - Segmentos de dirección formateada: se recorta la cola provincia/país.
 */
export function candidatosPoblacionPlaces(parts) {
  const out = [];
  const add = (raw, { desdeDireccion = false } = {}) => {
    let s = String(raw || "").trim();
    if (!s) return;
    s = s.replace(/^\d{5}\s+/, "").trim();
    if (!s || s.length < 2 || /^\d+$/.test(s)) return;
    if (VIA_PREFIX_RE.test(s)) return;
    // Solo descartar provincia si viene de un tramo de dirección, no del town explícito.
    if (desdeDireccion && esNombreProvinciaOPais(s)) return;
    const n = normLocalidadNombre(s);
    if (out.some((x) => normLocalidadNombre(x) === n)) return;
    out.push(s);
  };

  if (Array.isArray(parts?.poblacion_candidatos)) {
    parts.poblacion_candidatos.forEach((c) => add(c, { desdeDireccion: false }));
  }
  add(parts?.poblacion, { desdeDireccion: false });
  add(parts?.barrio, { desdeDireccion: false });

  for (const blob of [parts?.suggestion_text, parts?.direccion].filter(Boolean)) {
    for (const seg of segmentosLocalidadDesdeTexto(blob, parts?.provincia)) {
      add(seg, { desdeDireccion: true });
    }
  }

  return out;
}

/**
 * Entre varios hits del catálogo, preferir el núcleo/pedanía más específico:
 * aparece antes en la dirección (…, Llosa de Camacho, Alcalalí, …) o nombre más largo.
 */
function elegirHitPoblacionMasEspecifico(hits, parts, cands) {
  if (!hits?.length) return null;
  if (hits.length === 1) return hits[0];
  const blob = normLocalidadNombre(
    segmentosLocalidadDesdeTexto(
      [parts?.suggestion_text, parts?.direccion].filter(Boolean).join(", "),
      parts?.provincia,
    )
      .concat(cands || [])
      .join(" , "),
  );
  let best = null;
  for (const h of hits) {
    const pn = normLocalidadNombre(h.nombre);
    const pnSin = sinArticuloLocalidad(h.nombre);
    let idx = blob ? blob.indexOf(pn) : -1;
    if (idx < 0 && pnSin) idx = blob ? blob.indexOf(pnSin) : -1;
    const scoreIdx = idx >= 0 ? idx : 99999;
    const len = Math.max(pn.length, pnSin.length);
    if (
      !best
      || scoreIdx < best.scoreIdx
      || (scoreIdx === best.scoreIdx && len > best.len)
    ) {
      best = { id: h.id, nombre: h.nombre, scoreIdx, len };
    }
  }
  return best ? { id: best.id, nombre: best.nombre } : hits[0];
}

/**
 * Primer(es) candidato(s) que existen en el catálogo CRM.
 * Si hay varios (p. ej. pedanía + municipio), gana el más específico.
 * @returns {{ id: string, nombre: string }}
 */
export function matchPoblacionPlacesEnLista(parts, poblaciones) {
  const lista = Array.isArray(poblaciones) ? poblaciones : [];
  const cands = candidatosPoblacionPlaces(parts);

  // 1) Todos los candidatos que existen en catálogo (no el primero a ciegas:
  //    Google locality=Alcalalí y sugerencia/Llosa → ambos; preferir Llosa).
  const hits = [];
  for (const cand of cands) {
    const id = resolverPoblacionIdEnLista(cand, lista);
    if (!id) continue;
    if (hits.some((h) => h.id === id)) continue;
    const hit = lista.find((p) => normalizePoblacionId(p.id) === id);
    hits.push({ id, nombre: hit?.nombre || cand });
  }
  const elegido = elegirHitPoblacionMasEspecifico(hits, parts, cands);
  if (elegido?.id) return elegido;

  // 2) Catálogo en texto de localidad (sin cola provincia/país).
  const blob = normLocalidadNombre(
    segmentosLocalidadDesdeTexto(
      [parts?.suggestion_text, parts?.direccion].filter(Boolean).join(", "),
      parts?.provincia,
    )
      .concat(cands)
      .join(" , "),
  );
  if (blob) {
    let best = null;
    for (const p of lista) {
      const base = String(p.nombre || "");
      const ets = [base];
      if (base.includes("/")) {
        base.split("/").forEach((x) => {
          const t = x.trim();
          if (t) ets.push(t);
        });
      }
      for (const et of ets) {
        const pn = normLocalidadNombre(et);
        const pnSin = sinArticuloLocalidad(et);
        if (pn.length < 4 && pnSin.length < 4) continue;
        let idx = blob.indexOf(pn);
        if (idx < 0 && pnSin.length >= 4) idx = blob.indexOf(pnSin);
        if (idx < 0) continue;
        const len = Math.max(pn.length, pnSin.length);
        if (!best || idx < best.idx || (idx === best.idx && len > best.len)) {
          best = { id: normalizePoblacionId(p.id), nombre: p.nombre, len, idx };
        }
      }
    }
    if (best) return { id: best.id, nombre: best.nombre };
  }

  return { id: "", nombre: resolverPoblacionPlaces(parts) };
}
